import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import type { ConfigItemViewModel } from '../api';
import { SourceBadge } from './SourceBadge';

interface AdminConfigsTableProps {
  readonly configs: readonly ConfigItemViewModel[];
  /** Row click opens the detail drawer for that key. */
  readonly onSelect: (config: ConfigItemViewModel) => void;
  /** Key of the currently open row, highlighted as selected. */
  readonly selectedKey: string | null;
}

export function AdminConfigsTable({
  configs,
  onSelect,
  selectedKey,
}: AdminConfigsTableProps): ReactElement {
  const { t } = useTranslation();
  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <th className="w-6 px-3 py-2 font-medium">
              <span className="sr-only">{t('routes.admin.configs.table.overridden')}</span>
            </th>
            <th className="px-3 py-2 font-medium w-1/4">{t('routes.admin.configs.table.key')}</th>
            <th className="px-3 py-2 font-medium">{t('routes.admin.configs.table.value')}</th>
            <th className="px-3 py-2 font-medium">{t('routes.admin.configs.table.default')}</th>
            <th className="px-3 py-2 font-medium w-1/4">{t('routes.admin.configs.table.description')}</th>
            <th className="px-3 py-2 font-medium w-24">{t('routes.admin.configs.table.source')}</th>
          </tr>
        </thead>
        <tbody>
          {configs.map((config) => (
            <ConfigRow
              key={config.key}
              config={config}
              onSelect={onSelect}
              isSelected={selectedKey === config.key}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface ConfigRowProps {
  readonly config: ConfigItemViewModel;
  readonly onSelect: (config: ConfigItemViewModel) => void;
  readonly isSelected: boolean;
}

function ConfigRow({ config, onSelect, isSelected }: ConfigRowProps): ReactElement {
  const { t } = useTranslation();
  const overridden = config.source === 'DB';
  const displayValue = config.secret
    ? t('routes.admin.configs.value.secret')
    : (config.value ?? t('routes.admin.configs.value.unset'));
  const displayDefault = config.secret
    ? t('routes.admin.configs.value.secret')
    : (config.defaultValue ?? t('routes.admin.configs.value.unset'));

  return (
    <tr
      className={cn(
        'cursor-pointer border-b border-border last:border-0 align-top hover:bg-muted/40 focus-within:bg-muted/40',
        isSelected && 'bg-muted/60',
      )}
    >
      <td className="px-3 py-2 text-center">
        {overridden && (
          <span
            className="font-semibold text-primary"
            title={t('routes.admin.configs.row.overriddenTitle')}
            aria-label={t('routes.admin.configs.row.overriddenTitle')}
          >
            *
          </span>
        )}
      </td>
      <td className="px-3 py-2 font-mono text-xs">
        <button
          type="button"
          onClick={() => {
            onSelect(config);
          }}
          className="block w-full break-all text-left hover:underline focus:outline-none"
          aria-label={t('routes.admin.configs.row.openAria', { key: config.key })}
        >
          {config.key}
        </button>
      </td>
      <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
        <span className="break-all">{displayValue}</span>
      </td>
      <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
        <span className="break-all">{displayDefault}</span>
      </td>
      <td className="px-3 py-2 text-xs text-muted-foreground">
        {config.description !== null && config.description !== '' ? (
          <span className="line-clamp-2">{config.description}</span>
        ) : (
          <span className="text-muted-foreground/60">
            {t('routes.admin.configs.value.unset')}
          </span>
        )}
      </td>
      <td className="px-3 py-2">
        <SourceBadge source={config.source} />
      </td>
    </tr>
  );
}
